import { z } from "zod";

import { memberPhoneSchema } from "./phone.ts";

export const registerSchema = z
  .object({
    /**
     * 🔴 **注册的手机号支持各个地区**（老板 2026-08-31：「手机号调整为支持不同
     *    地区…默认台湾，然后是中国，下面是其他地区」）。存进库的是 **E.164**
     *    （`+8613700137000`），规则吃 `libphonenumber-js`，见 `phone.ts`。
     * ⚠️ 别退回那条只认大陆 11 位的正则 —— 那样台湾/香港的号一律注册不了。
     */
    phone: memberPhoneSchema.optional(),
    email: z.email("邮箱格式不对").optional(),
    password: z.string().min(8, "密码至少 8 位").max(72),
    nickname: z.string().max(32).optional(),
    /** 从哪个分销商的专属链接进来的 */
    inviteCode: z.string().length(8).optional(),
    visitId: z.number().int().positive().optional(),
    /** 人机验证的解（PoW payload，base64）。配了 CAPTCHA_HMAC_KEY 时必填。 */
    captchaPayload: z.string().max(4096).optional(),
    /** 短信验证码。配了短信通道时必填。 */
    smsCode: z.string().max(12).optional(),
  })
  .refine((v) => v.phone || v.email, { message: "手机号和邮箱至少填一个" });
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  /** 手机号或邮箱 */
  identifier: z.string().min(1, "请输入手机号或邮箱"),
  password: z.string().min(1, "请输入密码"),
  /**
   * 人机验证的解。平时不用带 —— 只有服务端判定这个 IP 像在撞库时
   * 才会回 CAPTCHA_REQUIRED，前端补上验证再重试一次。
   */
  captchaPayload: z.string().max(4096).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** 发短信验证码。🔴 票要绑到这个号码上，见 sendSmsCode 的注释。 */
export const sendSmsCodeSchema = z.object({
  /** ⚠️ 跟注册同一套规则（E.164 + libphonenumber），否则台湾号收不到码。 */
  phone: memberPhoneSchema,
  purpose: z.enum(["register", "reset", "bind"]),
  captchaPayload: z.string().max(4096),
});

export const changePasswordSchema = z.object({
  oldPassword: z.string().min(1),
  newPassword: z.string().min(8, "新密码至少 8 位").max(72),
});

export const sessionUserSchema = z.object({
  id: z.number().int(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  nickname: z.string(),
  avatarKey: z.string().nullable(),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

/**
 * 我持有的身份。
 *
 * 🔴 一个账号可能同时是买家、某经销商的 owner、另一个分销商的员工 ——
 *    这是这个平台的常态，前端要据此渲染"切换身份"。
 */
export const identitySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  type: z.enum(["platform", "supplier", "dealer", "distributor"]),
  status: z.enum(["pending", "active", "suspended", "closed"]),
  role: z.enum(["owner", "admin", "staff"]),
  levelKey: z.string(),
});

export const myIdentitiesSchema = z.object({
  boundOrgId: z.number().int().nullable(),
  /**
   * 我的专属服务商（归属节点），带名字。
   *
   * 🔴 只回 id 的话前端渲染不出「你的专属服务商是 XX」——
   *    而这条恰恰是这个平台跟普通商城最不一样的地方，藏起来等于没做。
   * ⚠️ 已清退的节点回 null：他的店进不去，显示出来只会让用户点了报错。
   */
  binding: z
    .object({ id: z.number().int(), name: z.string(), type: z.string() })
    .nullable(),
  orgs: z.array(identitySchema),
});

export type Identity = z.infer<typeof identitySchema>;
export type MyIdentities = z.infer<typeof myIdentitiesSchema>;

export const addressSchema = z.object({
  id: z.number().int(),
  receiver: z.string().min(1).max(32),
  /**
   * 收货人电话。
   *
   * 🔴 **故意比登录手机号宽松，别改成 `memberPhoneSchema`。**
   *    它是**给司机打的联系方式**，不是登录凭据：
   *    - 结算页那个框没有地区选择器（也不该有 —— 下单流程不该多一步），
   *      所以用户打进来的就是 `13800138000` 这种裸号码；
   *      按 E.164 那套校验的话，默认地区是台湾，裸的 11 位会被判成非法，
   *      **每一个大陆用户都下不了单**。
   *    - 收货人可能根本不是本人（写工地上工长的号、写座机、写分机），
   *      拿"必须是手机号"去卡它没有道理。
   *    这条是被 `verify:api` 的「建地址 / 下单成功」抓出来的。
   */
  phone: z
    .string()
    .trim()
    .min(6, "电话太短了")
    .max(20)
    .regex(/^[0-9+\-() ]+$/, "电话只能是数字和 + - ( ) 空格"),
  province: z.string().min(1),
  city: z.string().min(1),
  district: z.string().min(1),
  detail: z.string().min(1).max(120),
  isDefault: z.boolean(),
});

export type Address = z.infer<typeof addressSchema>;

export const upsertAddressSchema = addressSchema.omit({ id: true }).extend({
  id: z.number().int().optional(),
});
export type UpsertAddressInput = z.infer<typeof upsertAddressSchema>;
